import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Avatar,
  Divider,
  TextField,
  IconButton,
} from "@mui/material";
import {
  People as PeopleIcon,
  Straighten as StraightenIcon,
  AccessTime as AccessTimeIcon,
  Star as StarIcon,
  Chat as ChatIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useState } from "react";

/**
 * Dialog component for displaying the details of a related dive group
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when closing the dialog
 * @param {Object} props.group - The dive group data object
 * @param {Function} props.onAddComment - Function to call when adding a comment
 */
function RelatedDiveGroupDialog({ open, onClose, group, onAddComment }) {
  // State for new comments
  const [newComments, setNewComments] = useState({});

  // Return null if no group data is available
  if (!group || !group.dives) return null;
  console.log("Related Dive Group dialog - Group data:", group);

  // Handle comment input change for a specific dive
  const handleCommentChange = (diveId, value) => {
    setNewComments({
      ...newComments,
      [diveId]: value,
    });
  };

  // Handle comment submission
  const handleSubmitComment = (diveId) => {
    if (newComments[diveId]?.trim()) {
      onAddComment(diveId, newComments[diveId]);
      // Clear the input after submission
      setNewComments({
        ...newComments,
        [diveId]: "",
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Related Dive Group</Typography>
          <Chip
            icon={<PeopleIcon />}
            label={`${group.dives.length} Divers`}
            color="primary"
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {group.dives.length > 0 ? (
          <>
            <Typography variant="h5" gutterBottom>
              {group.dives[0]?.divesite.uddf.divesite.site?.name || "Dive Site"}
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {group.dives[0]?.divesite?.uddf.divesite.site.geography.address
                .city || ""}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Divers in your divegroup.
            </Typography>

            <Grid container spacing={2}>
              {group.dives.map((dive) => (
                <Grid item xs={12} sm={6} key={dive._id}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar
                        src={dive.diver?.profilePicture}
                        sx={{ width: 40, height: 40, mr: 2 }}
                      >
                        {dive.diver?.displayName?.[0] || "?"}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1">
                          {dive.diver?.displayName || "Diver"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(dive.diveDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {dive.maxDepth && (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <StraightenIcon
                            fontSize="small"
                            sx={{ mr: 1, color: "text.secondary" }}
                          />
                          <Typography variant="body2">
                            {dive.maxDepth}m depth
                          </Typography>
                        </Box>
                      )}
                      {dive.diveDuration && (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <AccessTimeIcon
                            fontSize="small"
                            sx={{ mr: 1, color: "text.secondary" }}
                          />
                          <Typography variant="body2">
                            {dive.diveDuration} minutes
                          </Typography>
                        </Box>
                      )}
                      {dive.diveRating && (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <StarIcon
                            fontSize="small"
                            sx={{ mr: 1, color: "text.secondary" }}
                          />
                          <Typography variant="body2">
                            {dive.diveRating}/5 rating
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    {dive.seenItems && dive.seenItems.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Sightings:
                        </Typography>
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {dive.seenItems.slice(0, 5).map((item) => (
                            <Chip
                              key={item._id}
                              label={item.commonName}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                          {dive.seenItems.length > 5 && (
                            <Chip
                              label={`+${dive.seenItems.length - 5} more`}
                              size="small"
                              variant="outlined"
                              sx={{ mb: 0.5 }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Comments Section */}
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <ChatIcon
                          fontSize="small"
                          sx={{ mr: 1, color: "text.secondary" }}
                        />
                        <Typography variant="body2" color="textSecondary">
                          Comments
                        </Typography>
                      </Box>

                      {dive.comments ? (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            mb: 2,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2">
                            {dive.comments}
                          </Typography>
                        </Paper>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          No comments yet
                        </Typography>
                      )}

                      {/* Add Comment Form
                      <Box sx={{ display: "flex", mt: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Add a comment..."
                          fullWidth
                          variant="outlined"
                          value={newComments[dive._id] || ""}
                          onChange={(e) =>
                            handleCommentChange(dive._id, e.target.value)
                          }
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleSubmitComment(dive._id);
                            }
                          }}
                        />
                        <IconButton
                          color="primary"
                          onClick={() => handleSubmitComment(dive._id)}
                          disabled={!newComments[dive._id]?.trim()}
                        >
                          <SendIcon />
                        </IconButton>
                      </Box>  */}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <Typography color="textSecondary" align="center">
            No dive information available for this group.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default RelatedDiveGroupDialog;
